"use client";
import { useEffect } from "react";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { ImageFragment, PostPreview } from "../article-card";

interface PostsFilterProps {
  posts: PostPreview[];
}

export type PostsFilterOption =
  | "All"
  | "Business Insights"
  | "Industrial Hazards"
  | "Infrastructure"
  | "Natural Hazards"
  | "Pollution"
  | "Products";

export type PostsSortOption =
  | "Alphabetical"
  | "Newest to Oldest"
  | "Oldest to Newest"
  | "Sort";
export const filterOptions: PostsFilterOption[] = [
  "All",
  "Business Insights",
  "Industrial Hazards",
  "Infrastructure",
  "Natural Hazards",
  "Pollution",
  "Products",
];
export const sortOptions: PostsSortOption[] = [
  "Alphabetical",
  "Newest to Oldest",
  "Oldest to Newest",
];

export interface PressItem {
  __typename: "GCMS_PressItem";
  title: React.ReactNode;
  description: React.ReactNode;
  date?: any | null;
  type:
    | "blog"
    | "in_the_news"
    | "news"
    | "news_brief"
    | "podcast"
    | "press_release"
    | "recent_press"
    | "release"
    | "report"
    | "white_paper";
  info?: { __typename: "GCMS_PressItem_Link"; link: string } | null;
  logo?: ImageFragment & { alt?: string };
  tags?: string[];
}

export interface PressPostFilterProps {
  pressItems: PressItem[];
}

export type PressPostsFilterOption =
  | "All"
  | "In the News"
  | "Press Release"
  | "Podcast"
  | "Blog"
  | "White Paper"
  | "";

export const pressFilterOptions: PressPostsFilterOption[] = [
  "All",
  "In the News",
  "Press Release",
  "Podcast",
  "Blog",
  "White Paper",
];

export interface PartialPostsProps {
  __typename: "GCMS_Post";
  excerpt: string;
  title: string;
  slug: string;
  blogCoverImage: any;
  aggregatePostPage: {
    title: string;
  };
  date: string;
}
export interface PartialPressItemProps {
  __typename: "GCMS_PressItem";
  title: string;
  description: string;
  logo: any;
  type: string;
  info: {
    link: string;
  };
  date: string;
}

export const postsControllerAtom = atom<{
  search: string;
  filter: PostsFilterOption;
  sort: PostsSortOption;
}>({
  search: "",
  filter: "All",
  sort: "Sort",
});

export const pressPostsControllerAtom = atom<{
  filter: PressPostsFilterOption;
  sort: PostsSortOption;
}>({
  filter: "All",
  sort: "Sort",
});

export const finalResultAtom = atom<
  PostPreview[] | PressItem[] | (PartialPostsProps | PartialPressItemProps)[]
>([]);
export const postsSelectorAtom = atom((get) => {
  const finalResult = get(finalResultAtom);

  let filterArr = [...finalResult];
  if (filterArr.length === 0) return [];

  const isPress =
    (filterArr as PressItem[])[0]?.__typename === "GCMS_PressItem";

  const controller = isPress
    ? get(pressPostsControllerAtom)
    : get(postsControllerAtom);

  // --- Filter by controller.filter ---
  if (isPress) {
    const pressFilter = controller.filter;
    if (pressFilter !== "All") {
      filterArr = (filterArr as PressItem[]).filter(
        (post) => post.type.replace(/_/g, " ") === pressFilter.toLowerCase(),
      );
    }
  } else {
    const postFilter = controller.filter;
    if (postFilter !== "All") {
      filterArr = (filterArr as PostPreview[]).filter(
        (post) => post.aggregatePostPage?.title === postFilter,
      );
    }
  }

  // --- Filter by controller.search (Only for non-press) ---
  if (!isPress && "search" in controller) {
    const searchTerm = controller.search.toLowerCase();
    if (searchTerm) {
      filterArr = (filterArr as PostPreview[]).filter(
        (post) =>
          post.title.toLowerCase().includes(searchTerm) ||
          post.excerpt?.toLowerCase().includes(searchTerm) ||
          post.author?.person?.name.toLowerCase().includes(searchTerm),
      );
    }
  }

  // --- Sort by controller.sort ---
  const sortOption = controller.sort;

  switch (sortOption) {
    case "Alphabetical":
      return !isPress
        ? [...(filterArr as PostPreview[])].sort((a, b) =>
            (a?.title as string)?.localeCompare(b.title as string),
          )
        : [...(filterArr as PressItem[])].sort((a, b) =>
            String(a.title).localeCompare(String(b.title)),
          );

    case "Newest to Oldest":
      return !isPress
        ? [...(filterArr as PostPreview[])].sort(
            (a, b) =>
              new Date(b.published).getTime() - new Date(a.published).getTime(),
          )
        : [...(filterArr as PressItem[])].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
          );

    case "Oldest to Newest":
      return !isPress
        ? [...(filterArr as PostPreview[])].sort(
            (a, b) =>
              new Date(a.published).getTime() - new Date(b.published).getTime(),
          )
        : [...(filterArr as PressItem[])].sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
          );

    case "Sort":
    default:
      if (!isPress) {
        return [...(filterArr as PostPreview[])].sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );
      } else {
        const pressArr = filterArr as PressItem[];
        const featured = pressArr
          .filter((p) => p?.tags?.includes("featured"))
          .sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
          );
        const others = pressArr
          .filter((p) => !p?.tags?.includes("featured"))
          .sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
          );
        return featured.concat(others);
      }
  }
});

export const highlightedSearchAtom = atom<string>("");

export function usePostsFilter(
  prop:
    | PostsFilterProps
    | PressPostFilterProps
    | (PartialPostsProps | PartialPressItemProps)[],
  from: string,
  preselectedFilter?: string,
) {
  const setPosts = useSetAtom(finalResultAtom);
  const [controller, setPostsController] = useAtom(postsControllerAtom);
  const [pressController, setPressPostsController] = useAtom(
    pressPostsControllerAtom,
  );

  const selectedPosts: any = useAtomValue(postsSelectorAtom);
  const setHighlightQuery = useSetAtom(highlightedSearchAtom);

  useEffect(() => {
    setPosts(
      from === "news-room"
        ? (prop as PressPostFilterProps)?.pressItems
        : from === "knowledge-center"
          ? (prop as PostsFilterProps)?.posts
          : (prop as (PartialPostsProps | PartialPressItemProps)[]),
    );
  }, [prop]);

  useEffect(() => {
    if (from === "news-room" && preselectedFilter) {
      setPressPostsController({
        ...pressController,
        filter: preselectedFilter as PressPostsFilterOption,
      });
    }
  }, [preselectedFilter]);

  const onSortChange = (e: any) => {
    from === "news-room"
      ? setPressPostsController({
          ...pressController,
          sort: e.target.value as PostsSortOption,
        })
      : from === "knowledge-center"
        ? setPostsController({
            ...controller,
            sort: e.target.value as PostsSortOption,
          })
        : "";
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPostsController({
      ...controller,
      search: e.target.value,
    });
    setHighlightQuery(e.target.value);
  };

  const onFilterChange = (e: React.ChangeEvent<HTMLButtonElement>) => {
    from === "news-room"
      ? setPressPostsController({
          ...pressController,
          filter: e.target.value as PressPostsFilterOption,
        })
      : setPostsController({
          ...controller,
          filter: e.target.value as PostsFilterOption,
        });
  };

  return {
    onSortChange,
    onFilterChange,
    onSearchChange,
    selectedPosts,
    filterOptions:
      from === "news-room"
        ? pressFilterOptions.slice(1)
        : filterOptions.slice(1),
    sortOptions,
  };
}
